<template>
  <div class="space-y-24">
    <div class="text-static-default-mid body-1 flex md:justify-center">
      {{ i18n(translations.pageDescription) }}
    </div>

    <RevInfoBlock
      v-if="firstOnHoldPolicy"
      :content="i18n(translations.updatePaymentMethodDescription)"
      :icon="IconWarning"
      :title="firstOnHoldPolicy.productTitle"
      variant="danger"
    >
      <RevButton
        size="small"
        target="_blank"
        :to="{
          type: 'internal',
          name: INSURANCE.DUE_PAYMENT,
          params: {
            policyId: firstOnHoldPolicy.policyId,
          },
        }"
        variant="primary"
      >
        {{ i18n(translations.updatePaymentMethodButton) }}
      </RevButton>
    </RevInfoBlock>

    <div v-if="!isEmpty(eligibleInsurances)" class="space-y-24">
      <h3 class="text-static-default-hi heading-3">
        {{ i18n(translations.eligibleDevices) }}
      </h3>

      <EligibleInsuranceCard
        v-for="eligibleInsurance in sortedEligibleInsurances"
        :key="eligibleInsurance.orderlineId"
        :eligible-insurance
      />

      <h3 class="text-static-default-hi heading-3">
        {{ i18n(translations.yourProtectionPlans) }}
      </h3>
    </div>

    <div class="space-y-24">
      <PolicyCard
        v-for="policy in sortedPolicies"
        :key="policy.policyId"
        :policy
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import { INSURANCE } from '~/scopes/insurance/routes.constants'

import EligibleInsuranceCard from '../../components/EligibleInsuranceCard/EligibleInsuranceCard.vue'
import type { EligibleInsurance, Policy } from '../../types'

import translations from './PoliciesAndEligibleInsurances.translations'
import PolicyCard from './components/PolicyCard/PolicyCard.vue'

const props = defineProps<{
  policies: Policy[]
  eligibleInsurances: EligibleInsurance[]
}>()

const i18n = useI18n()

const firstOnHoldPolicy = computed(() =>
  props.policies.find((policy) => policy.state === 'ON_HOLD'),
)

const sortedPolicies = computed(() => {
  const stateOrder: Policy['state'][] = [
    'ON_HOLD',
    'ACTIVE',
    'NOT_STARTED',
    'CANCELLED',
    'EXPIRED',
  ]

  return [...props.policies].sort(
    (a, b) => stateOrder.indexOf(a.state) - stateOrder.indexOf(b.state),
  )
})

const sortedEligibleInsurances = computed(() => {
  return [...props.eligibleInsurances].sort(
    (a, b) =>
      new Date(a.eligibilityEndDate).getTime() -
      new Date(b.eligibilityEndDate).getTime(),
  )
})
</script>
